// frontend/src/components/UserProfile.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ProgrammingLanguage } from '../contexts/FirebaseAuthContext';
import { LoginModal } from './LoginModal';
import { UserIcon } from './CustomIcons';
import { UsageIndicator } from './UsageIndicator';
import { clearPendingAction } from '../utils/authRedirectState';

const LANGUAGE_OPTIONS: { value: ProgrammingLanguage; label: string }[] = [
  { value: 'python', label: 'Python' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'java', label: 'Java' },
  { value: 'golang', label: 'Go' },
];

export const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated, isLoading, signOut, updatePreferredLanguage } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debug auth state
  useEffect(() => {
    console.log('[UserProfile] Auth state:', {
      isLoading,
      isAuthenticated,
      hasUserProfile: !!userProfile,
      userEmail: userProfile?.email
    });
  }, [isLoading, isAuthenticated, userProfile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    signOut();
    setIsDropdownOpen(false);
  };

  const handleLanguageChange = async (language: ProgrammingLanguage) => {
    if (!userProfile || language === userProfile.preferredLanguage) return;

    setIsUpdatingLanguage(true);
    try {
      await updatePreferredLanguage(language);
      console.log(`Language preference updated to ${language}`);
    } catch (error) {
      console.error('Failed to update language preference:', error);
      alert('Failed to update language preference. Please try again.');
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  // Don't show anything while auth state is still loading
  if (isLoading) {
    console.log('[UserProfile] Still loading auth state...');
    return null;
  }

  if (!isAuthenticated || !userProfile) {
    const handleSignInClick = () => {
      // Clear any stale pending actions from previous sessions
      // This is a DIRECT sign-in, not from an auth-required action
      clearPendingAction();
      console.log('[UserProfile] Cleared stale pending actions for direct sign-in');
      setShowLoginModal(true);
    };

    return (
      <>
        <button
          className="signin-button"
          title="Sign In"
          onClick={handleSignInClick}
        >
          <UserIcon size={24} color="white" />
          <span className="signin-text">Sign In</span>
        </button>

        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => setShowLoginModal(false)}
        />
      </>
    );
  }

  return (
    <div className="user-profile" ref={dropdownRef}>
      <button
        className="profile-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        title={`Signed in as ${userProfile.displayName}`}
      >
        {userProfile.photoURL ? (
          <img
            src={userProfile.photoURL}
            alt={userProfile.displayName}
            className="profile-picture"
          />
        ) : (
          <div className="profile-picture" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#6c757d' }}>
            <UserIcon size={20} color="white" />
          </div>
        )}
      </button>

      {isDropdownOpen && (
        <div className="profile-dropdown">
          <div className="profile-info">
            {userProfile.photoURL ? (
              <img src={userProfile.photoURL} alt={userProfile.displayName} className="dropdown-picture" />
            ) : (
              <div className="dropdown-picture" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#6c757d' }}>
                <UserIcon size={28} color="white" />
              </div>
            )}
            <div className="profile-details">
              <div className="profile-name">{userProfile.displayName}</div>
              <div className="profile-email">{userProfile.email}</div>
            </div>
          </div>

          <UsageIndicator />

          <div className="dropdown-divider"></div>
          
          <div className="language-preferences">
            <div className="dropdown-section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="currentColor"/>
              </svg>
              Programming Language
            </div>
            <div className="language-options">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`language-option ${userProfile.preferredLanguage === option.value ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(option.value)}
                  disabled={isUpdatingLanguage}
                >
                  <span>{option.label}</span>
                  {userProfile.preferredLanguage === option.value && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {isUpdatingLanguage && (
              <div className="language-updating">
                <div className="spinner"></div>
                <span>Updating...</span>
              </div>
            )}
          </div>
          
          <div className="dropdown-divider"></div>
          
          {/* Hidden for v1 release - feature not ready */}
          <button
            className="dropdown-item hidden-for-v1"
            onClick={() => {
              navigate('/reviews?round=1');
              setIsDropdownOpen(false);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L13.5 2.5L16.17 5.17C15.24 5.06 14.32 5 13.4 5H12C8.86 5 6.03 6.5 4 9V11H6.5V16L10.5 20H13.5L9.5 16V11H10.5C11.25 11 12 11.17 12.74 11.5L21 9ZM7.5 12C6.67 12 6 12.67 6 13.5S6.67 15 7.5 15 9 14.33 9 13.5 8.33 12 7.5 12Z" fill="currentColor"/>
            </svg>
            Quick Review Session
          </button>
          
          <button className="dropdown-item" onClick={handleSignOut}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7Z" fill="currentColor"/>
              <path d="M4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};